import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Wallet, Banknote, ArrowDownRight, ArrowUpRight, Scale, FileBarChart, RefreshCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { toast } from 'sonner';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#a78bfa', '#ef4444', '#06b6d4'];

const AGING_KEYS = [
  { k: 'current', label: 'Current', color: '#10b981' },
  { k: '0_15', label: '0–15 days', color: '#3b82f6' },
  { k: '16_30', label: '16–30', color: '#f59e0b' },
  { k: '31_60', label: '31–60', color: '#fb923c' },
  { k: '61_90', label: '61–90', color: '#ef4444' },
  { k: 'over_90', label: '90+', color: '#9f1239' },
];

export default function FinanceDashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(today);
  const [kpi, setKpi] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [tb, setTb] = useState(null);
  const [ar, setAr] = useState(null);
  const [ap, setAp] = useState(null);
  const [cash, setCash] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    setBusy(true);
    try {
      const q = `?date_from=${from}&date_to=${to}`;
      const [k, p, b, t, a, ap2, c] = await Promise.all([
        axios.get(`${API}/finance/kpis${q}`, { withCredentials: true }),
        axios.get(`${API}/accounting/pnl`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/finance/balance-sheet`, { withCredentials: true }),
        axios.get(`${API}/finance/trial-balance`, { withCredentials: true }),
        axios.get(`${API}/finance/ar-aging`, { withCredentials: true }),
        axios.get(`${API}/finance/ap-aging`, { withCredentials: true }),
        axios.get(`${API}/finance/cashflow?months=12`, { withCredentials: true }),
      ]);
      setKpi(k.data); setPnl(p.data); setBs(b.data); setTb(t.data); setAr(a.data); setAp(ap2.data); setCash(c.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [from, to]);

  const pnlMonths = (pnl?.months || []).map((m) => ({
    month: `${pnl?.year}-${String(m.month).padStart(2, '0')}`,
    Revenue: m.income,
    Expenses: m.expense + (m.payroll || 0),
    Net: m.net,
  }));
  const assetMix = bs ? [
    { name: 'Cash', value: bs.assets.cash },
    { name: 'AR', value: bs.assets.accounts_receivable },
    { name: 'Inventory', value: bs.assets.inventory },
    { name: 'Fixed', value: bs.assets.fixed_assets },
  ].filter((x) => x.value > 0) : [];
  const liabilityMix = bs ? [
    { name: 'AP', value: bs.liabilities.accounts_payable },
    { name: 'Equity', value: bs.equity.total },
  ].filter((x) => x.value > 0) : [];
  const arBuckets = ar ? AGING_KEYS.map(({ k, label, color }) => ({ label, value: ar.buckets[k] || 0, color })) : [];
  const apBuckets = ap ? AGING_KEYS.map(({ k, label, color }) => ({ label, value: ap.buckets[k] || 0, color })) : [];

  return (
    <Layout>
      <div data-testid="finance-dashboard" className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-900 dark:text-slate-100"><FileBarChart className="w-7 h-7 text-emerald-500" />Financial Dashboard</h1>
            <p className="text-sm text-muted-foreground dark:text-slate-400">Standard finance reports — P&L, Balance Sheet, Trial Balance, AR/AP Aging, Cashflow.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">From</Label><Input data-testid="fin-from" type="date" className="h-9 w-[140px]" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div><Label className="text-xs">To</Label><Input data-testid="fin-to" type="date" className="h-9 w-[140px]" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            <Button data-testid="fin-refresh" size="sm" variant="outline" onClick={fetchAll} disabled={busy}><RefreshCcw className="w-3 h-3 mr-1" />Refresh</Button>
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Card data-testid="fin-kpi-revenue" className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"><div className="text-xs text-emerald-700 uppercase tracking-wide">Revenue</div><div className="text-xl font-bold mt-1 text-emerald-900">{fmt(kpi?.revenue)}</div></Card>
          <Card data-testid="fin-kpi-expense" className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200"><div className="text-xs text-rose-700 uppercase tracking-wide">Expenses</div><div className="text-xl font-bold mt-1 text-rose-900">{fmt(kpi?.expense)}</div></Card>
          <Card data-testid="fin-kpi-net" className="p-4 bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200"><div className="text-xs text-sky-700 uppercase tracking-wide">Net Profit</div><div className={`text-xl font-bold mt-1 ${kpi?.net_profit >= 0 ? 'text-sky-900' : 'text-rose-900'}`}>{fmt(kpi?.net_profit)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground uppercase tracking-wide">Cash Balance</div><div className="text-xl font-bold mt-1">{fmt(kpi?.cash_balance)}</div></Card>
          <Card className="p-4 bg-amber-50 dark:bg-amber-900/20"><div className="text-xs text-amber-700 uppercase tracking-wide">Receivable</div><div className="text-xl font-bold mt-1 text-amber-900 dark:text-amber-200">{fmt(kpi?.accounts_receivable)}</div></Card>
          <Card className="p-4 bg-orange-50 dark:bg-orange-900/20"><div className="text-xs text-orange-700 uppercase tracking-wide">Payable</div><div className="text-xl font-bold mt-1 text-orange-900 dark:text-orange-200">{fmt(kpi?.accounts_payable)}</div></Card>
        </div>

        {/* P&L 12-month */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Banknote className="w-4 h-4 text-emerald-500" />Profit & Loss — 12 month</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={pnlMonths}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Revenue" fill="#10b981" />
              <Bar dataKey="Expenses" fill="#ef4444" />
              <Line type="monotone" dataKey="Net" stroke="#3b82f6" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Balance sheet */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><Scale className="w-4 h-4 text-sky-500" />Assets — {fmt(bs?.assets?.total)}</h2>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={assetMix} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(d) => `${d.name}: ${fmt(d.value)}`}>
                  {assetMix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><Scale className="w-4 h-4 text-rose-500" />Liabilities & Equity — {fmt((bs?.liabilities?.total || 0) + (bs?.equity?.total || 0))}</h2>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={liabilityMix} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(d) => `${d.name}: ${fmt(d.value)}`}>
                  {liabilityMix.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </div>

        {/* Trial Balance */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="font-semibold">Trial Balance — as of {tb?.as_of}</h2>
            <span className={`text-xs font-semibold ${tb?.is_balanced ? 'text-emerald-600' : 'text-rose-600'}`}>{tb?.is_balanced ? '✓ Balanced' : '⚠ Out of balance'}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase text-slate-600"><tr><th className="px-3 py-2 text-left">Code</th><th className="text-left">Account</th><th>Type</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
              <tbody>
                {(tb?.rows || []).map((r, i) => (
                  <tr key={i} data-testid={`tb-row-${i}`} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{r.code || '—'}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="text-center capitalize">{r.type || '—'}</td>
                    <td className="text-right">{r.debit > 0 ? fmt(r.debit) : '—'}</td>
                    <td className="text-right">{r.credit > 0 ? fmt(r.credit) : '—'}</td>
                  </tr>
                ))}
                <tr className="border-t font-semibold bg-slate-50 dark:bg-slate-900/50"><td colSpan={3} className="px-3 py-2 text-right">Total</td><td className="text-right">{fmt(tb?.total_debit)}</td><td className="text-right">{fmt(tb?.total_credit)}</td></tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* AR/AP aging */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><ArrowDownRight className="w-4 h-4 text-emerald-500" />AR Aging — {fmt(ar?.total)}</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={arBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="value">{arBuckets.map((b, i) => <Cell key={i} fill={b.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr><th className="text-left">Customer</th>{AGING_KEYS.map(b => <th key={b.k} className="text-right">{b.label}</th>)}<th className="text-right">Total</th></tr></thead>
                <tbody>
                  {(ar?.rows || []).slice(0, 8).map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`ar-row-${i}`}>
                      <td className="py-1 truncate max-w-[180px]" title={r.customer}>{r.customer}</td>
                      {AGING_KEYS.map(b => <td key={b.k} className="text-right">{r[b.k] > 0 ? fmt(r[b.k]) : '—'}</td>)}
                      <td className="text-right font-semibold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card className="p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-rose-500" />AP Aging — {fmt(ap?.total)}</h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={apBuckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Bar dataKey="value">{apBuckets.map((b, i) => <Cell key={i} fill={b.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground"><tr><th className="text-left">Vendor</th>{AGING_KEYS.map(b => <th key={b.k} className="text-right">{b.label}</th>)}<th className="text-right">Total</th></tr></thead>
                <tbody>
                  {(ap?.rows || []).slice(0, 8).map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`ap-row-${i}`}>
                      <td className="py-1 truncate max-w-[180px]" title={r.vendor}>{r.vendor}</td>
                      {AGING_KEYS.map(b => <td key={b.k} className="text-right">{r[b.k] > 0 ? fmt(r[b.k]) : '—'}</td>)}
                      <td className="text-right font-semibold">{fmt(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Cashflow */}
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-indigo-500" />Cash flow — 12 month</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={cash?.months || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="inflow" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="outflow" stroke="#ef4444" strokeWidth={2} />
              <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </Layout>
  );
}
